import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { CopyLink } from '@hvcgroep/design-system';
import useICalUrl from '../../../../../shared/hooks/useICalUrl';
import Button from '../../../../../shared/components/Button';

import { MOZILLA_LIGHTNING_INFO, OUTLOOK_INFO } from '../../../config/constants';

import './style.scss';
import gtm from '../../../../../shared/helpers/gtm';
import { APP_ID } from '../../../../../shared/config/constants';

const BlockCtaPersonalAgenda: React.FC = () => {
  const { t } = useTranslation();
  const classNames = c('BlockCtaPersonalAgenda', 'cta-block', 'cta-block--no-image', 'o-background--grey');
  const calendarUrl = useICalUrl();

  const [unfolded, setUnfolded] = React.useState(false);

  const translationBasePath = 'waste_calendar.content.cta.personal_agenda';

  const getSteps = (path: 'google_calendar' | 'windows_live_mail_agenda'): string[] => {
    return t(`${translationBasePath}.${path}.steps`, { returnObjects: true });
  };

  return (
    <article className={classNames}>
      <div className="cta-block__text">
        <h2 className="BlockCtaPersonalAgenda__title">
          {t(`${translationBasePath}.title`)}
        </h2>

        {unfolded && (
          <div className="BlockCtaPersonalAgenda__content">
            <p>{t(`${translationBasePath}.intro`)}</p>

            <div className="BlockCtaPersonalAgenda__paragraph">
              <h3>{t(`${translationBasePath}.apple_icloud_calendar.title`)}</h3>
              <p>{t(`${translationBasePath}.apple_icloud_calendar.body`)}</p>
              {calendarUrl && (
                <Button type="secondary" icon="arrow_forward" href={calendarUrl.replace('https://', 'webcal://')}>
                  {t(`${translationBasePath}.apple_icloud_calendar.button`)}
                </Button>
              )}
            </div>

            <div className="BlockCtaPersonalAgenda__paragraph">
              <h3>{t(`${translationBasePath}.google_calendar.title`)}</h3>
              <p>{t(`${translationBasePath}.google_calendar.body`)}</p>
              <ul>
                {getSteps('google_calendar').map((item) => (
                  <li>{item}</li>
                ))}
              </ul>
              {calendarUrl && (
                <CopyLink url={calendarUrl} />
              )}
            </div>

            <div className="BlockCtaPersonalAgenda__paragraph">
              <h3>{t(`${translationBasePath}.windows_live_mail_agenda.title`)}</h3>
              <p>{t(`${translationBasePath}.windows_live_mail_agenda.body`)}</p>
              <ul>
                {getSteps('windows_live_mail_agenda').map((item) => (
                  <li>{item}</li>
                ))}
              </ul>
              {calendarUrl && (
                <CopyLink url={calendarUrl} />
              )}
            </div>

            <div className="BlockCtaPersonalAgenda__paragraph">
              <h3>{t(`${translationBasePath}.outlook_or_mozilla.title`)}</h3>
              <p>{t(`${translationBasePath}.outlook_or_mozilla.body`)}</p>
              <ul>
                <li>
                  <a href={String(OUTLOOK_INFO)} target="_blank" rel="noreferrer noopener">
                    {t(`${translationBasePath}.outlook_or_mozilla.links.outlook`)}
                  </a>
                </li>
                <li>
                  <a href={String(MOZILLA_LIGHTNING_INFO)} target="_blank" rel="noreferrer noopener">
                    {t(`${translationBasePath}.outlook_or_mozilla.links.mozilla`)}
                  </a>
                </li>
              </ul>
              {calendarUrl && (
                <CopyLink url={calendarUrl} />
              )}
            </div>
          </div>
        )}

        <Button
          type="primary"
          icon={unfolded ? 'arrow_upward' : undefined}
          variation={!unfolded ? 'anchor' : undefined}
          className="BlockCtaPersonalAgenda__toggle-unfolded"
          noTracking
          onClick={() => {
            // Send tracking event to GTM.
            gtm.trackUserEvent(String(APP_ID), 'Personal agenda CTA click', unfolded ? 'closed' : 'opened');

            // Unfold the block.
            setUnfolded(!unfolded);
          }}
        >
          {t(`${translationBasePath}.button.show_${unfolded ? 'less' : 'more'}`)}
        </Button>
      </div>
    </article>
  );
};

export default BlockCtaPersonalAgenda;
